// =============================================================
// KeywordsPage  Phase 7+ Step 1: Keywords Product Module
// Customer-facing keyword intelligence powered by Google Search Console.
// Real metrics only, deterministic opportunities, zero fabricated data.
// =============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  businessApi,
  keywordsApi,
  Business,
  KeywordsResponseDto,
  KeywordDetailResponseDto,
  KeywordPositionBucket,
  KeywordSortField,
  SortOrder,
  KeywordDateRange,
  KeywordItemDto,
  KeywordOpportunityDto,
} from '../lib/api';

export default function KeywordsPage() {
  const { businessId } = useParams<{ businessId?: string }>();
  const navigate = useNavigate();

  // Active Business State
  const [business, setBusiness] = useState<Business | null>(null);
  const [activeBizId, setActiveBizId] = useState<number | null>(
    businessId ? parseInt(businessId, 10) : null
  );

  // Data State
  const [data, setData] = useState<KeywordsResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filters & Controls
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [positionBucket, setPositionBucket] = useState<KeywordPositionBucket>('all');
  const [dateRange, setDateRange] = useState<KeywordDateRange>('28d');
  const [sortBy, setSortBy] = useState<KeywordSortField>('clicks');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Detail Modal State
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
  const [keywordDetail, setKeywordDetail] = useState<KeywordDetailResponseDto | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Resolve businessId if missing from URL, or update when businessId changes
  useEffect(() => {
    if (!businessId) {
      const stored = localStorage.getItem('active_dashboard_business_id');
      if (stored) {
        navigate(`/businesses/${stored}/keywords`, { replace: true });
        setActiveBizId(parseInt(stored, 10));
      } else {
        businessApi
          .list()
          .then((bizList) => {
            if (bizList.length > 0) {
              navigate(`/businesses/${bizList[0].id}/keywords`, { replace: true });
              setActiveBizId(bizList[0].id);
            } else {
              setLoading(false);
            }
          })
          .catch((err) => {
            console.error('Failed to load businesses:', err);
            setError('Unable to load business profile. Please try again.');
            setLoading(false);
          });
      }
    } else {
      const parsedId = parseInt(businessId, 10);
      if (!isNaN(parsedId)) {
        setActiveBizId(parsedId);
        localStorage.setItem('active_dashboard_business_id', String(parsedId));
      }
    }
  }, [businessId, navigate]);

  // Listen for active-business-change event from TopBar or Navigation
  useEffect(() => {
    function handleBizChange(e: Event) {
      const customEvent = e as CustomEvent<number>;
      if (customEvent.detail && customEvent.detail !== activeBizId) {
        setData(null);
        setActiveBizId(customEvent.detail);
        navigate(`/businesses/${customEvent.detail}/keywords`);
      }
    }

    window.addEventListener('active-business-change', handleBizChange);
    return () => {
      window.removeEventListener('active-business-change', handleBizChange);
    };
  }, [activeBizId, navigate]);

  // Debounce search query input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Load business info and keywords data
  const loadKeywords = useCallback(async () => {
    if (!activeBizId) return;
    setLoading(true);
    setError(null);
    setData(null);

    try {
      // 1. Fetch Business Profile
      const biz = await businessApi.get(activeBizId);
      setBusiness(biz);

      // 2. Fetch Keywords Data
      const res = await keywordsApi.getKeywords(activeBizId, {
        dateRange,
        search: debouncedSearch,
        positionBucket,
        sortBy,
        sortOrder,
        page,
        pageSize,
      });
      setData(res);
    } catch (err: any) {
      console.error('Failed to fetch keywords:', err);
      setError(err?.message || 'Unable to load keyword data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeBizId, dateRange, debouncedSearch, positionBucket, sortBy, sortOrder, page, pageSize]);

  useEffect(() => {
    loadKeywords();
  }, [loadKeywords]);

  // Sync / Refresh Search Console data
  async function handleSync() {
    if (!activeBizId || syncing) return;
    setSyncing(true);
    setSyncMessage(null);
    setError(null);

    try {
      const res = await keywordsApi.sync(activeBizId);
      setSyncMessage(res.message || 'Keywords data synchronized successfully.');
      setTimeout(() => setSyncMessage(null), 4000);
      await loadKeywords();
    } catch (err: any) {
      console.error('Sync failed:', err);
      setError(err?.message || 'Failed to sync Search Console data. Please try again.');
    } finally {
      setSyncing(false);
    }
  }

  // Open Keyword Detail Drawer/Modal
  async function handleOpenDetail(query: string) {
    if (!activeBizId) return;
    setSelectedKeyword(query);
    setLoadingDetail(true);
    setDetailError(null);
    setKeywordDetail(null);

    try {
      const detail = await keywordsApi.getKeywordDetail(activeBizId, query);
      setKeywordDetail(detail);
    } catch (err: any) {
      console.error('Failed to load keyword detail:', err);
      setDetailError(err?.message || 'Unable to load keyword details.');
    } finally {
      setLoadingDetail(false);
    }
  }

  function handleCloseDetail() {
    setSelectedKeyword(null);
    setKeywordDetail(null);
    setDetailError(null);
  }

  // Sort toggle helper
  function handleSort(field: KeywordSortField) {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'position' ? 'asc' : 'desc');
    }
    setPage(1);
  }

  const getPositionBadgeClass = (pos: number) => {
    if (pos <= 3) return 'badge-success';
    if (pos <= 10) return 'badge-info';
    if (pos <= 20) return 'badge-warning';
    return 'badge-neutral';
  };

  const getOpportunityBadgeStyle = (type: string) => {
    switch (type) {
      case 'PAGE_2_OPPORTUNITY':
        return { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' };
      case 'HIGH_IMPRESSIONS_LOW_CTR':
        return { background: '#ffedd5', color: '#9a3412', border: '1px solid #fb923c' };
      case 'TOP_10_OPPORTUNITY':
        return { background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' };
      case 'HIGH_CLICKS':
        return { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' };
      default:
        return { background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' };
    }
  };

  const isConnected = data?.connected ?? false;
  const summary = data?.summary;
  const keywords = data?.keywords ?? [];
  const opportunities = data?.opportunities ?? [];
  const pagination = data?.pagination;

  return (
    <DashboardLayout
      title={business ? `${business.name}  Keywords` : 'Keywords'}
      subtitle="Track and analyze the search queries bringing customers to your business."
      breadcrumbs={[
        { label: 'Dashboard', to: '/dashboard' },
        {
          label: business?.name || 'Business',
          to: business ? `/businesses/${business.id}/dashboard` : '/businesses',
        },
        { label: 'Keywords' },
      ]}
    >
      <div className="dashboard-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>
        {/* Navigation Breadcrumb Bar */}
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: '#64748b' }}>
            <Link to="/dashboard" style={{ color: '#64748b', textDecoration: 'none' }}>
              Dashboard
            </Link>
            <span>/</span>
            {business ? (
              <Link to={`/businesses/${business.id}/dashboard`} style={{ color: '#64748b', textDecoration: 'none' }}>
                {business.name}
              </Link>
            ) : (
              <span>Business</span>
            )}
            <span>/</span>
            <span style={{ color: '#0f172a', fontWeight: 600 }}>Keywords</span>
          </div>

          {/* Sync & Refresh Action */}
          {isConnected && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {data?.property?.lastSyncedAt && (
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                  Last synced: {new Date(data.property.lastSyncedAt).toLocaleString()}
                </span>
              )}
              <button
                id="refresh-keywords-btn"
                className="btn btn-secondary btn-sm"
                onClick={handleSync}
                disabled={syncing || loading}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <span style={{ display: 'inline-block', transform: syncing ? 'rotate(180deg)' : 'none', transition: 'transform 0.5s' }}>
                  
                </span>
                <span>{syncing ? 'Refreshing...' : 'Refresh Data'}</span>
              </button>
            </div>
          )}
        </div>

        {/* Success / Info Message */}
        {syncMessage && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              backgroundColor: '#ecfdf5',
              border: '1px solid #6ee7b7',
              color: '#065f46',
              marginBottom: '16px',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span></span>
            <span>{syncMessage}</span>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div
            style={{
              padding: '14px 18px',
              borderRadius: '8px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              marginBottom: '20px',
              fontSize: '0.9rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>{error}</span>
            <button
              onClick={loadKeywords}
              className="btn btn-secondary btn-sm"
              style={{ fontSize: '0.8rem', padding: '4px 10px' }}
            >
              Retry
            </button>
          </div>
        )}

        {/* LOADING STATE */}
        {loading && !data && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
            <div className="spinner" style={{ margin: '0 auto 16px', width: '32px', height: '32px' }} />
            <p style={{ margin: 0, fontWeight: 500 }}>Loading Search Console keyword data...</p>
          </div>
        )}

        {/* GOOGLE DISCONNECTED / EMPTY STATE */}
        {!loading && !isConnected && (
          <div
            className="card"
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              background: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              margin: '24px 0',
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}></div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f172a', margin: '0 0 10px' }}>
              Connect Google Search Console
            </h2>
            <p style={{ maxWidth: '480px', margin: '0 auto 24px', color: '#64748b', fontSize: '0.95rem', lineHeight: 1.5 }}>
              Connect Search Console to start tracking the search queries and keywords bringing visitors to your website.
            </p>
            {business ? (
              <Link
                to={`/businesses/${business.id}/integrations`}
                className="btn btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 22px', fontSize: '0.95rem' }}
              >
                <span>Connect Google Search Console</span>
                <span></span>
              </Link>
            ) : (
              <Link to="/businesses" className="btn btn-primary">
                Select Business
              </Link>
            )}
          </div>
        )}

        {/* CONNECTED AND READY */}
        {!loading && isConnected && (
          <>
            {/* 1. KEYWORD SUMMARY CARDS */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '16px',
                marginBottom: '24px',
              }}
            >
              {/* Total Keywords */}
              <div className="card" style={{ padding: '18px 20px', background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Total Keywords
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a', marginTop: '6px' }}>
                  {summary && summary.totalKeywords > 0 ? summary.totalKeywords.toLocaleString() : ''}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '4px' }}>Unique search queries</div>
              </div>

              {/* Top 10 Keywords */}
              <div className="card" style={{ padding: '18px 20px', background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Top 10 Keywords
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#16a34a', marginTop: '6px' }}>
                  {summary && summary.top10Keywords > 0 ? summary.top10Keywords.toLocaleString() : ''}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '4px' }}>Ranking on Page 1</div>
              </div>

              {/* Average Position */}
              <div className="card" style={{ padding: '18px 20px', background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Average Position
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a', marginTop: '6px' }}>
                  {summary && summary.averagePosition > 0 ? summary.averagePosition : ''}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '4px' }}>Across all ranked queries</div>
              </div>

              {/* Total Impressions */}
              <div className="card" style={{ padding: '18px 20px', background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Total Impressions
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#2563eb', marginTop: '6px' }}>
                  {summary && summary.totalImpressions > 0 ? summary.totalImpressions.toLocaleString() : ''}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '4px' }}>Google search views</div>
              </div>

              {/* Total Clicks */}
              <div className="card" style={{ padding: '18px 20px', background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Total Clicks
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a', marginTop: '6px' }}>
                  {summary && summary.totalClicks > 0 ? summary.totalClicks.toLocaleString() : ''}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '4px' }}>Organic site visits</div>
              </div>

              {/* Average CTR */}
              <div className="card" style={{ padding: '18px 20px', background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Average CTR
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a', marginTop: '6px' }}>
                  {summary && summary.averageCtr > 0 ? `${(summary.averageCtr * 100).toFixed(1)}%` : ''}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '4px' }}>Click-through rate</div>
              </div>
            </div>

            {/* 2. KEYWORD OPPORTUNITIES SECTION */}
            {opportunities.length > 0 && (
              <div
                className="card"
                style={{
                  marginBottom: '24px',
                  padding: '20px 24px',
                  background: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span></span>
                      <span>Keyword Opportunities</span>
                    </h2>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                      High-impact search queries with proven ranking momentum detected directly from your Search Console data.
                    </p>
                  </div>
                  {business && (
                    <Link
                      to={`/businesses/${business.id}/intelligence`}
                      style={{
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        color: '#2563eb',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <span>Explore in SEO Intelligence</span>
                      <span></span>
                    </Link>
                  )}
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '14px',
                  }}
                >
                  {opportunities.slice(0, 4).map((opp, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '14px 16px',
                        background: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                          <span
                            style={{
                              fontSize: '0.95rem',
                              fontWeight: 700,
                              color: '#0f172a',
                              wordBreak: 'break-word',
                            }}
                          >
                            "{opp.query}"
                          </span>
                          <span
                            style={{
                              ...getOpportunityBadgeStyle(opp.type),
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {opp.label}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '0.8rem', color: '#64748b', marginBottom: '8px' }}>
                          <span>Pos: <strong>{opp.position}</strong></span>
                          <span>Views: <strong>{opp.impressions}</strong></span>
                          <span>Clicks: <strong>{opp.clicks}</strong></span>
                          <span>CTR: <strong>{(opp.ctr * 100).toFixed(1)}%</strong></span>
                        </div>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569', lineHeight: 1.4 }}>
                          {opp.recommendedAction}
                        </p>
                      </div>

                      <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <button
                          onClick={() => handleOpenDetail(opp.query)}
                          className="btn btn-secondary btn-sm"
                          style={{ fontSize: '0.78rem', padding: '3px 8px' }}
                        >
                          View Trends
                        </button>
                        {business && (
                          <Link
                            to={`/businesses/${business.id}/intelligence`}
                            style={{ fontSize: '0.78rem', color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}
                          >
                            Investigate Opportunity 
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 3. FILTERS & SEARCH TOOLBAR */}
            <div
              className="card"
              style={{
                marginBottom: '16px',
                padding: '16px 20px',
                background: '#ffffff',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '14px',
              }}
            >
              {/* Search Box */}
              <div style={{ flex: '1 1 240px', minWidth: '220px', position: 'relative' }}>
                <input
                  id="keyword-search-input"
                  type="text"
                  placeholder="Search search queries..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 32px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#94a3b8',
                    fontSize: '0.9rem',
                    pointerEvents: 'none',
                  }}
                >
                  
                </span>
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                    }}
                  >
                    
                  </button>
                )}
              </div>

              {/* Position Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Position:</span>
                <select
                  id="position-filter-select"
                  value={positionBucket}
                  onChange={(e) => {
                    setPositionBucket(e.target.value as KeywordPositionBucket);
                    setPage(1);
                  }}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.85rem',
                    background: '#ffffff',
                    color: '#0f172a',
                    outline: 'none',
                  }}
                >
                  <option value="all">All Positions</option>
                  <option value="top3">Top 3 (13)</option>
                  <option value="4-10">Page 1 (410)</option>
                  <option value="11-20">Page 2 (1120)</option>
                  <option value="21-50">Pages 35 (2150)</option>
                  <option value="51+">51+</option>
                </select>
              </div>

              {/* Date Range Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Date Range:</span>
                <select
                  id="date-range-select"
                  value={dateRange}
                  onChange={(e) => {
                    setDateRange(e.target.value as KeywordDateRange);
                    setPage(1);
                  }}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.85rem',
                    background: '#ffffff',
                    color: '#0f172a',
                    outline: 'none',
                  }}
                >
                  <option value="7d">Last 7 days</option>
                  <option value="28d">Last 28 days</option>
                  <option value="3m">Last 3 months</option>
                  <option value="6m">Last 6 months</option>
                  <option value="all">All Time</option>
                </select>
              </div>

              {/* Sort By Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Sort:</span>
                <select
                  id="sort-by-select"
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as KeywordSortField);
                    setSortOrder(order as SortOrder);
                    setPage(1);
                  }}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.85rem',
                    background: '#ffffff',
                    color: '#0f172a',
                    outline: 'none',
                  }}
                >
                  <option value="clicks-desc">Clicks (High to Low)</option>
                  <option value="clicks-asc">Clicks (Low to High)</option>
                  <option value="impressions-desc">Impressions (High to Low)</option>
                  <option value="ctr-desc">CTR (High to Low)</option>
                  <option value="position-asc">Position (Best First)</option>
                  <option value="position-desc">Position (Lowest First)</option>
                  <option value="query-asc">Keyword (A-Z)</option>
                </select>
              </div>
            </div>

            {/* 4. KEYWORD TABLE */}
            <div
              className="card"
              style={{
                background: '#ffffff',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                marginBottom: '20px',
              }}
            >
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <th
                        onClick={() => handleSort('query')}
                        style={{ padding: '12px 16px', cursor: 'pointer', userSelect: 'none' }}
                      >
                        Keyword {sortBy === 'query' ? (sortOrder === 'asc' ? '' : '') : ''}
                      </th>
                      <th
                        onClick={() => handleSort('clicks')}
                        style={{ padding: '12px 16px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                      >
                        Clicks {sortBy === 'clicks' ? (sortOrder === 'asc' ? '' : '') : ''}
                      </th>
                      <th
                        onClick={() => handleSort('impressions')}
                        style={{ padding: '12px 16px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                      >
                        Impressions {sortBy === 'impressions' ? (sortOrder === 'asc' ? '' : '') : ''}
                      </th>
                      <th
                        onClick={() => handleSort('ctr')}
                        style={{ padding: '12px 16px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                      >
                        CTR {sortBy === 'ctr' ? (sortOrder === 'asc' ? '' : '') : ''}
                      </th>
                      <th
                        onClick={() => handleSort('position')}
                        style={{ padding: '12px 16px', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                      >
                        Position {sortBy === 'position' ? (sortOrder === 'asc' ? '' : '') : ''}
                      </th>
                      <th style={{ padding: '12px 16px' }}>Opportunity</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', padding: '48px 16px', color: '#64748b' }}>
                          <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}></div>
                          <p style={{ margin: 0, fontWeight: 500 }}>No keywords found matching the selected criteria.</p>
                          {(search || positionBucket !== 'all') && (
                            <button
                              onClick={() => {
                                setSearch('');
                                setPositionBucket('all');
                              }}
                              className="btn btn-secondary btn-sm"
                              style={{ marginTop: '12px' }}
                            >
                              Clear Filters
                            </button>
                          )}
                        </td>
                      </tr>
                    ) : (
                      keywords.map((kw: KeywordItemDto, index: number) => (
                        <tr
                          key={index}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'background-color 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          {/* Keyword Query */}
                          <td style={{ padding: '14px 16px' }}>
                            <div style={{ fontWeight: 600, color: '#0f172a' }}>{kw.query}</div>
                            {kw.pages && kw.pages.length > 0 && (
                              <div
                                style={{
                                  fontSize: '0.78rem',
                                  color: '#94a3b8',
                                  marginTop: '2px',
                                  maxWidth: '360px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={kw.pages[0]}
                              >
                                {kw.pages[0]}
                              </div>
                            )}
                          </td>

                          {/* Clicks */}
                          <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                            {kw.clicks.toLocaleString()}
                          </td>

                          {/* Impressions */}
                          <td style={{ padding: '14px 16px', textAlign: 'right', color: '#475569' }}>
                            {kw.impressions.toLocaleString()}
                          </td>

                          {/* CTR */}
                          <td style={{ padding: '14px 16px', textAlign: 'right', color: '#475569' }}>
                            {(kw.ctr * 100).toFixed(1)}%
                          </td>

                          {/* Position Badge */}
                          <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontSize: '0.82rem',
                                fontWeight: 700,
                                background:
                                  kw.position <= 3
                                    ? '#dcfce7'
                                    : kw.position <= 10
                                    ? '#dbeafe'
                                    : kw.position <= 20
                                    ? '#fef3c7'
                                    : '#f1f5f9',
                                color:
                                  kw.position <= 3
                                    ? '#15803d'
                                    : kw.position <= 10
                                    ? '#1d4ed8'
                                    : kw.position <= 20
                                    ? '#b45309'
                                    : '#64748b',
                              }}
                            >
                              {kw.position}
                            </span>
                          </td>

                          {/* Opportunity */}
                          <td style={{ padding: '14px 16px' }}>
                            {kw.opportunity ? (
                              <span
                                style={{
                                  ...getOpportunityBadgeStyle(kw.opportunity.type),
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                }}
                                title={kw.opportunity.description}
                              >
                                {kw.opportunity.label}
                              </span>
                            ) : (
                              <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}></span>
                            )}
                          </td>

                          {/* Action */}
                          <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                            <button
                              onClick={() => handleOpenDetail(kw.query)}
                              className="btn btn-secondary btn-sm"
                              style={{ fontSize: '0.8rem', padding: '4px 10px' }}
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* 5. PAGINATION CONTROLS */}
              {pagination && pagination.totalPages > 1 && (
                <div
                  style={{
                    padding: '14px 20px',
                    borderTop: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px',
                    fontSize: '0.85rem',
                    color: '#64748b',
                  }}
                >
                  <div>
                    Showing <strong>{(pagination.page - 1) * pagination.pageSize + 1}</strong> to{' '}
                    <strong>
                      {Math.min(pagination.page * pagination.pageSize, pagination.total)}
                    </strong>{' '}
                    of <strong>{pagination.total}</strong> keywords
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={pagination.page <= 1}
                      className="btn btn-secondary btn-sm"
                    >
                       Previous
                    </button>
                    <span style={{ padding: '0 4px', fontWeight: 600, color: '#0f172a' }}>
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={pagination.page >= pagination.totalPages}
                      className="btn btn-secondary btn-sm"
                    >
                      Next 
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* 6. KEYWORD DETAIL DRAWER / MODAL */}
        {selectedKeyword && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              zIndex: 1000,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
            onClick={handleCloseDetail}
          >
            <div
              style={{
                width: '100%',
                maxWidth: '640px',
                height: '100%',
                backgroundColor: '#ffffff',
                boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                boxSizing: 'border-box',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drawer Header */}
              <div
                style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f8fafc',
                }}
              >
                <div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                    Keyword Details
                  </div>
                  <h3 style={{ margin: '4px 0 0', fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                    "{selectedKeyword}"
                  </h3>
                </div>
                <button
                  onClick={handleCloseDetail}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '1.25rem',
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                >
                  
                </button>
              </div>

              {/* Drawer Content */}
              <div style={{ padding: '24px', flex: 1 }}>
                {loadingDetail && (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                    <div className="spinner" style={{ margin: '0 auto 12px', width: '28px', height: '28px' }} />
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>Loading keyword breakdown...</p>
                  </div>
                )}

                {detailError && (
                  <div style={{ padding: '12px 16px', background: '#fef2f2', color: '#991b1b', borderRadius: '8px' }}>
                    {detailError}
                  </div>
                )}

                {!loadingDetail && keywordDetail && (
                  <>
                    {/* Performance Metrics Summary */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '10px',
                        marginBottom: '20px',
                      }}
                    >
                      <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>CLICKS</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', marginTop: '4px' }}>
                          {keywordDetail.totalClicks}
                        </div>
                      </div>
                      <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>IMPRESSIONS</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#2563eb', marginTop: '4px' }}>
                          {keywordDetail.totalImpressions}
                        </div>
                      </div>
                      <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>CTR</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', marginTop: '4px' }}>
                          {(keywordDetail.averageCtr * 100).toFixed(1)}%
                        </div>
                      </div>
                      <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>AVG POS</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#16a34a', marginTop: '4px' }}>
                          {keywordDetail.averagePosition}
                        </div>
                      </div>
                    </div>

                    {/* Daily Historical Performance Table */}
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                        Historical Daily Performance
                      </h4>
                      {keywordDetail.dailyMetrics && keywordDetail.dailyMetrics.length > 0 ? (
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                                <th style={{ padding: '8px 12px' }}>Date</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Clicks</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Impressions</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>CTR</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Position</th>
                              </tr>
                            </thead>
                            <tbody>
                              {keywordDetail.dailyMetrics.slice(-10).map((d, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '8px 12px', color: '#475569' }}>{d.date}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{d.clicks}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b' }}>{d.impressions}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b' }}>{(d.ctr * 100).toFixed(1)}%</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{d.position}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>
                          Historical trend data is not available for this property yet.
                        </p>
                      )}
                    </div>

                    {/* Landing Pages */}
                    {keywordDetail.topPages && keywordDetail.topPages.length > 0 && (
                      <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                          Associated Landing Pages
                        </h4>
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                                <th style={{ padding: '8px 12px' }}>Page URL</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Clicks</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Impressions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {keywordDetail.topPages.map((p, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '8px 12px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.page}>
                                    <a href={p.page} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                      {p.page}
                                    </a>
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{p.clicks}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b' }}>{p.impressions}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Device & Country Breakdown */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      {keywordDetail.devices && keywordDetail.devices.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>
                            Devices
                          </h4>
                          <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px', fontSize: '0.82rem' }}>
                            {keywordDetail.devices.map((dev, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: i < keywordDetail.devices.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                <span style={{ textTransform: 'capitalize', color: '#475569' }}>{dev.device.toLowerCase()}</span>
                                <span style={{ fontWeight: 600 }}>{dev.clicks} clicks ({dev.impressions} views)</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {keywordDetail.countries && keywordDetail.countries.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>
                            Countries
                          </h4>
                          <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px', fontSize: '0.82rem' }}>
                            {keywordDetail.countries.map((c, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: i < keywordDetail.countries.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                <span style={{ textTransform: 'uppercase', color: '#475569' }}>{c.country}</span>
                                <span style={{ fontWeight: 600 }}>{c.clicks} clicks</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
